/**
 * Formatage de montants africains avec espace insécable.
 * Backend renvoie les montants en BigInt centimes (string) — convertis ici.
 */

const toAmount = (cents: bigint | number | string): number => {
  if (typeof cents === 'string') return Number(BigInt(cents)) / 100;
  if (typeof cents === 'bigint') return Number(cents) / 100;
  return cents / 100;
};

const NBSP = ' ';

/** "12 500 000 FCFA" — espace insécable */
export const formatFCFA = (cents: bigint | number | string): string =>
  new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toAmount(cents)) + NBSP + 'FCFA';

/** "47 800 000 GNF" */
export const formatGNF = (cents: bigint | number | string): string =>
  new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(toAmount(cents)) + NBSP + 'GNF';

/** "12,5M FCFA" — pour les KPI cards condensées */
export const formatMillions = (cents: bigint | number | string): string => {
  const m = toAmount(cents) / 1_000_000;
  return m.toFixed(1).replace('.', ',') + 'M' + NBSP + 'FCFA';
};

/**
 * Montant en francs, arrondi à l'unité, sans suffixe de devise.
 *
 * Le franc CFA n'a pas de subdivision en usage : afficher « 629 524,1 FCFA »
 * n'a pas de sens comptable et rompt l'alignement avec les autres montants du
 * tableau de bord, tous entiers.
 *
 * À préférer à formatNumber() pour tout montant. formatNumber reste le bon
 * choix pour les quantités, où la décimale est légitime (12,5 m³ de béton).
 */
export const formatMontant = (cents: bigint | number | string): string =>
  new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(toAmount(cents));

/** Formatage générique sans devise */
export const formatNumber = (n: number | bigint | string): string => {
  const v = typeof n === 'string' ? Number(n) : typeof n === 'bigint' ? Number(n) : n;
  return new Intl.NumberFormat('fr-FR').format(v);
};

/** Détecte si une devise est convertie (versions FCFA du backend) */
export const isFcfaConverted = (currency: string): boolean => currency !== 'FCFA';
